'use client';
import type { VariantProps } from 'class-variance-authority';
import type { MouseEvent } from 'react';

import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const BackdropStyled = cnva('bg-transparent-70 absolute inset-0', {
  variants: {
    blur: {
      none: '',
      sm: 'backdrop-blur-sm',
      md: 'backdrop-blur-md',
    },
    disabledAnimate: {
      true: '',
      false: 'animate-fade-in',
    },
  },
  defaultVariants: {
    blur: 'md',
    disabledAnimate: false,
  },
});

export interface BackdropProps {
  /**
   * The class name for the backdrop.
   */
  className?: string;
  /**
   * Whether to apply a blur effect to the backdrop.
   */
  blur?: VariantProps<typeof BackdropStyled>['blur'];
  /**
   * Whether to disable the transition animation.
   */
  disabledAnimate?: boolean;
  /**
   * The function to call when the backdrop is click.
   */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  /**
   * The function to call when the backdrop is hover.
   */
  onMouseEnter?: () => void;
}

export const Backdrop = ({
  className,
  blur = 'md',
  disabledAnimate = false,
  onClick,
  onMouseEnter,
  ...rest
}: BackdropProps) => {
  return (
    <div
      role="presentation"
      className={BackdropStyled({
        blur,
        disabledAnimate,
        className,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      {...createDataAttributes({ test: 'backdrop' })}
      {...rest}
    />
  );
};
